import { useEffect, useState } from 'react';
import type { ClearChatOptions } from '../schemas';
import styles from './ClearChatDialog.module.css';

interface ClearChatDialogProps {
  open: boolean;
  memberName: string;
  onCancel: () => void;
  onConfirm: (options: ClearChatOptions) => void;
  pending?: boolean;
}

export function ClearChatDialog({
  open,
  memberName,
  onCancel,
  onConfirm,
  pending,
}: ClearChatDialogProps) {
  const [clearMessages, setClearMessages] = useState(true);
  const [clearFiles, setClearFiles] = useState(true);
  const [clearChatPins, setClearChatPins] = useState(false);

  useEffect(() => {
    if (open) {
      setClearMessages(true);
      setClearFiles(true);
      setClearChatPins(false);
    }
  }, [open]);

  const everything = clearMessages && clearFiles && clearChatPins;
  const anyChecked = clearMessages || clearFiles || clearChatPins;

  function handleEverythingChange(checked: boolean) {
    setClearMessages(checked);
    setClearFiles(checked);
    setClearChatPins(checked);
  }

  if (!open) return null;

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true">
      <div className={styles.panel}>
        <h2 className={styles.heading}>Clear chat</h2>
        <p className={styles.subtitle}>Choose what to clear from this chat with {memberName}.</p>

        <ul className={styles.options}>
          <li className={styles.option}>
            <label>
              <input
                type="checkbox"
                checked={clearMessages}
                onChange={(e) => setClearMessages(e.target.checked)}
              />
              <span className={styles.optionLabel}>Messages</span>
            </label>
            <p className={styles.optionHelp}>The conversation state of this chat.</p>
          </li>

          <li className={styles.option}>
            <label>
              <input
                type="checkbox"
                checked={clearFiles}
                onChange={(e) => setClearFiles(e.target.checked)}
              />
              <span className={styles.optionLabel}>Files</span>
            </label>
            <p className={styles.optionHelp}>
              Files you&#39;ve uploaded in this chat. Files still linked elsewhere are kept; orphans
              are deleted.
            </p>
          </li>

          <li className={styles.option}>
            <label>
              <input
                type="checkbox"
                checked={clearChatPins}
                onChange={(e) => setClearChatPins(e.target.checked)}
              />
              <span className={styles.optionLabel}>Chat Memories</span>
            </label>
            <p className={styles.optionHelp}>
              All the items you&#39;ve asked {memberName} to remember in this chat.
            </p>
          </li>

          <li className={`${styles.option} ${styles.optionEverything}`}>
            <label>
              <input
                type="checkbox"
                checked={everything}
                onChange={(e) => handleEverythingChange(e.target.checked)}
              />
              <span className={styles.optionLabel}>Everything</span>
            </label>
            <p className={styles.optionHelp}>Clear all of the above.</p>
          </li>
        </ul>

        <div className={styles.actions}>
          <button type="button" className={styles.cancel} onClick={onCancel} disabled={pending}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.confirm}
            onClick={() => onConfirm({ clearMessages, clearFiles, clearChatPins })}
            disabled={!anyChecked || pending}
          >
            {pending ? 'Clearing…' : 'Clear chat'}
          </button>
        </div>
      </div>
    </div>
  );
}
